import {
  SpectatorEvent as SpectatorEventSchema,
  SpectatorEventBatch as SpectatorEventBatchSchema,
  SpectatorServerMessage as SpectatorServerMessageSchema,
  SpectatorSnapshot as SpectatorSnapshotSchema,
  type SpectatorEvent as ProtocolSpectatorEvent,
  type SpectatorSnapshot as ProtocolSpectatorSnapshot,
} from "@guandan/protocol";

export type SpectatorEvent = ProtocolSpectatorEvent;
export type SpectatorSnapshot = ProtocolSpectatorSnapshot;
export type SpectatorSeat = 0 | 1 | 2 | 3;

export interface SpectatorFrame {
  readonly snapshot: SpectatorSnapshot;
  readonly events: readonly SpectatorEvent[];
}

export type SpectatorAccessInput = (
  | { readonly roomId: string; readonly code?: never }
  | { readonly code: string; readonly roomId?: never }
) & {
  readonly inviteToken?: string;
  readonly password?: string;
};

export type SpectatorConnectionState =
  "idle" | "connecting" | "live" | "polling" | "closed";

export interface RemoteSpectatorAuthorityOptions {
  readonly fetch?: typeof globalThis.fetch;
  readonly createWebSocket?: (url: string) => WebSocket;
  readonly connectionTimeoutMs?: number;
  readonly pollingIntervalMs?: number;
  readonly requestTimeoutMs?: number;
}

type TimerHandle = ReturnType<typeof globalThis.setTimeout>;

const MAX_RECENT_EVENTS = 24;
const MAX_EVENT_SYNC_BATCHES = 32;
const SPECTATOR_EVENT_TYPES = new Set<SpectatorEvent["type"]>([
  "GAME_STARTED",
  "ROUND_STARTED",
  "CARDS_PLAYED",
  "PLAYER_PASSED",
  "PLAYER_FINISHED",
  "TRICK_RESET",
  "ROUND_SETTLED",
  "LEVEL_ADVANCED",
  "MATCH_SETTLED",
  "TRIBUTE_OFFERED",
  "ACE_RESET",
  "TRIBUTE_RECEIVED",
  "TRIBUTE_RETURNED",
  "ANTI_TRIBUTE",
  "GAME_PAUSED",
  "GAME_RESUMED",
  "CONTROL_CHANGED",
  "QUICK_PHRASE",
  "EMOTE",
]);

/**
 * Authenticated read-only transport. It intentionally has no send, audio,
 * replay or player-table API, so spectator code cannot submit game intents.
 */
export class RemoteSpectatorAuthority {
  private readonly fetcher: typeof globalThis.fetch;
  private readonly createWebSocket: (url: string) => WebSocket;
  private readonly connectionTimeoutMs: number;
  private readonly pollingIntervalMs: number;
  private readonly requestTimeoutMs: number;
  private readonly listeners = new Set<(frame: SpectatorFrame) => void>();
  private readonly statusListeners = new Set<
    (status: SpectatorConnectionState) => void
  >();
  private snapshot: SpectatorSnapshot | null = null;
  private events: SpectatorEvent[] = [];
  private socket: WebSocket | null = null;
  private pollTimer: TimerHandle | null = null;
  private connectPromise: Promise<SpectatorFrame> | null = null;
  private eventSyncPromise: Promise<void> | null = null;
  private eventCursor = -1;
  private eventFetchCursor = -1;
  private state: SpectatorConnectionState = "idle";
  private membershipJoined = false;
  private transportClosed = false;
  private resolvedRoomId: string | null = null;

  constructor(
    private readonly websocketBaseUrl: string,
    private readonly apiBaseUrl: string,
    private readonly access: SpectatorAccessInput,
    private readonly token: string,
    options: RemoteSpectatorAuthorityOptions = {},
  ) {
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.createWebSocket =
      options.createWebSocket ?? ((url) => new WebSocket(url));
    this.connectionTimeoutMs = options.connectionTimeoutMs ?? 5_000;
    this.pollingIntervalMs = options.pollingIntervalMs ?? 2_000;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 8_000;
  }

  connect(): Promise<SpectatorFrame> {
    if (this.transportClosed)
      return Promise.reject(new Error("观战连接已关闭"));
    if (this.snapshot) return Promise.resolve(this.frame());
    if (this.connectPromise) return this.connectPromise;
    this.setState("connecting");
    const operation = this.joinMembership()
      .then(async () => {
        if (this.transportClosed) {
          await this.leaveMembership();
          throw new Error("观战连接已关闭");
        }
        try {
          const frame = await this.openSocket();
          this.setState("live");
          return frame;
        } catch {
          this.ensureOpen();
          const frame = await this.refreshFromHttp();
          this.startPolling();
          return frame;
        }
      })
      .finally(() => {
        if (this.connectPromise === operation) this.connectPromise = null;
      });
    this.connectPromise = operation;
    return operation;
  }

  subscribe(listener: (frame: SpectatorFrame) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeStatus(
    listener: (status: SpectatorConnectionState) => void,
  ): () => void {
    this.statusListeners.add(listener);
    listener(this.state);
    return () => this.statusListeners.delete(listener);
  }

  async close(): Promise<void> {
    if (!this.transportClosed) {
      this.transportClosed = true;
      this.setState("closed");
      this.stopPolling();
      const socket = this.socket;
      this.socket = null;
      try {
        socket?.close();
      } catch {
        /* transport already closed */
      }
    }
    await this.leaveMembership();
    this.listeners.clear();
    this.statusListeners.clear();
  }

  private async joinMembership(): Promise<void> {
    const target = this.access.roomId ?? this.access.code;
    const credentials = {
      ...(this.access.inviteToken
        ? { inviteToken: this.access.inviteToken }
        : {}),
      ...(this.access.password ? { password: this.access.password } : {}),
    };
    const value = await this.requestJson(
      `/rooms/${encodeURIComponent(target)}/spectators`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(credentials),
      },
      201,
    );
    const envelope = exactObject(value, ["membership"], "观战成员响应");
    const membership = exactObject(
      envelope.membership,
      ["roomId", "joinedAt"],
      "观战成员",
    );
    const roomId = requiredString(membership.roomId, "观战房间 ID");
    if (this.access.roomId && roomId !== this.access.roomId)
      throw new Error("观战成员房间不一致");
    requiredInteger(membership.joinedAt, "加入时间", 0);
    this.resolvedRoomId = roomId;
    this.membershipJoined = true;
  }

  private async leaveMembership(): Promise<void> {
    if (!this.membershipJoined) return;
    const response = await this.request(
      `/rooms/${encodeURIComponent(this.requireResolvedRoomId())}/spectators`,
      { method: "DELETE" },
    );
    if (response.status === 404 || (response.ok && response.status === 204)) {
      this.membershipJoined = false;
      return;
    }
    throw new Error("退出观战失败，请重试");
  }

  private async openSocket(): Promise<SpectatorFrame> {
    const roomId = this.requireResolvedRoomId();
    const ticketValue = await this.requestJson(
      `/rooms/${encodeURIComponent(roomId)}/spectator/socket-ticket`,
      { method: "POST" },
      201,
    );
    const ticketEnvelope = exactObject(
      ticketValue,
      ["ticket", "expiresAt"],
      "观战连接票据",
    );
    const ticket = requiredString(ticketEnvelope.ticket, "观战连接票据");
    if (!/^[A-Za-z0-9_-]{32,128}$/u.test(ticket))
      throw new Error("观战连接票据格式无效");
    requiredInteger(ticketEnvelope.expiresAt, "票据过期时间", 0);
    const url = `${this.websocketBaseUrl.replace(/\/$/u, "")}/rooms/${encodeURIComponent(roomId)}/spectator/connect?ticket=${encodeURIComponent(ticket)}`;
    return await new Promise<SpectatorFrame>((resolve, reject) => {
      let settled = false;
      let live = false;
      let socket: WebSocket;
      try {
        socket = this.createWebSocket(url);
      } catch (error) {
        reject(normalizeError(error, "无法建立只读观战连接"));
        return;
      }
      this.socket = socket;
      const timeout = globalThis.setTimeout(() => {
        if (settled) return;
        settled = true;
        try {
          socket.close();
        } catch {
          /* transport already closed */
        }
        reject(new Error("连接观战服务超时"));
      }, this.connectionTimeoutMs);
      const finish = (callback: () => void) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timeout);
        callback();
      };
      socket.addEventListener("message", (message) => {
        try {
          const parsed = parseSpectatorSocketMessage(
            JSON.parse(String(message.data)),
          );
          if (parsed === null) return;
          if (parsed.type === "EVENT") {
            this.acceptEvent(parsed.event);
            return;
          }
          const snapshot = assertSnapshotSemantics(parsed.snapshot, roomId);
          void this.syncEventsThrough(snapshot.eventSeq)
            .then(() => {
              this.acceptSnapshot(snapshot);
              live = true;
              finish(() => resolve(this.frame()));
            })
            .catch((error: unknown) => {
              if (!settled) {
                finish(() =>
                  reject(normalizeError(error, "无法同步观战公开事件")),
                );
                try {
                  socket.close();
                } catch {
                  /* transport already closed */
                }
              }
            });
        } catch (error) {
          finish(() => reject(normalizeError(error, "观战服务消息无效")));
          try {
            socket.close();
          } catch {
            /* transport already closed */
          }
        }
      });
      socket.addEventListener("close", () => {
        if (this.socket === socket) this.socket = null;
        if (!settled) {
          finish(() => reject(new Error("观战连接已断开")));
          return;
        }
        if (live && !this.transportClosed && this.snapshot?.phase !== "settled")
          void this.recoverWithHttp();
      });
      socket.addEventListener("error", () => {
        if (!settled) finish(() => reject(new Error("无法连接观战服务")));
      });
    });
  }

  private async recoverWithHttp(): Promise<void> {
    if (
      this.transportClosed ||
      this.pollTimer ||
      this.snapshot?.phase === "settled"
    )
      return;
    try {
      await this.refreshFromHttp();
    } catch {
      /* the bounded polling loop will retry */
    }
    this.startPolling();
  }

  private startPolling(): void {
    if (
      this.transportClosed ||
      this.pollTimer ||
      this.snapshot?.phase === "settled"
    )
      return;
    this.setState("polling");
    this.pollTimer = globalThis.setInterval(() => {
      void this.refreshFromHttp().catch(() => undefined);
    }, this.pollingIntervalMs);
  }

  private stopPolling(): void {
    if (!this.pollTimer) return;
    globalThis.clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private async refreshFromHttp(): Promise<SpectatorFrame> {
    const roomId = this.requireResolvedRoomId();
    const encoded = encodeURIComponent(roomId);
    const snapshotValue = await this.requestJson(
      `/rooms/${encoded}/spectator/snapshot`,
    );
    const snapshotEnvelope = exactObject(
      snapshotValue,
      ["snapshot"],
      "观战快照响应",
    );
    const parsedSnapshot = SpectatorSnapshotSchema.safeParse(
      snapshotEnvelope.snapshot,
    );
    if (!parsedSnapshot.success) throw new Error("观战快照响应无效");
    const snapshot = assertSnapshotSemantics(parsedSnapshot.data, roomId);
    await this.syncEventsThrough(snapshot.eventSeq);
    this.acceptSnapshot(snapshot);
    return this.frame();
  }

  private syncEventsThrough(targetEventSeq: number): Promise<void> {
    const previous =
      this.eventSyncPromise?.catch(() => undefined) ?? Promise.resolve();
    const operation = previous.then(async () => {
      if (this.transportClosed || targetEventSeq <= this.eventFetchCursor)
        return;
      const roomId = this.requireResolvedRoomId();
      const fetchedEvents: SpectatorEvent[] = [];
      let confirmedThrough = this.eventFetchCursor;
      for (
        let batch = 0;
        batch < MAX_EVENT_SYNC_BATCHES && confirmedThrough < targetEventSeq;
        batch += 1
      ) {
        const after = confirmedThrough;
        const value = await this.requestJson(
          `/rooms/${encodeURIComponent(roomId)}/spectator/events?after=${after}`,
        );
        const parsed = SpectatorEventBatchSchema.safeParse(value);
        if (!parsed.success) throw new Error("观战事件响应无效");
        if (parsed.data.throughSeq <= after)
          throw new Error("观战事件补流无进展");
        let previousEventSeq = after;
        for (const event of parsed.data.events) {
          if (
            event.seq <= previousEventSeq ||
            event.seq > parsed.data.throughSeq
          )
            throw new Error("观战事件顺序无效");
          previousEventSeq = event.seq;
          if (event.seq <= targetEventSeq) fetchedEvents.push(event);
        }
        confirmedThrough = Math.min(parsed.data.throughSeq, targetEventSeq);
      }
      if (confirmedThrough < targetEventSeq)
        throw new Error("观战事件补流未完成");
      for (const event of fetchedEvents) this.acceptEvent(event, false);
      this.eventFetchCursor = confirmedThrough;
      if (this.snapshot) this.publish();
    });
    const tracked = operation.finally(() => {
      if (this.eventSyncPromise === tracked) this.eventSyncPromise = null;
    });
    this.eventSyncPromise = tracked;
    return tracked;
  }

  private acceptSnapshot(snapshot: SpectatorSnapshot): void {
    if (this.snapshot && snapshot.version < this.snapshot.version) return;
    this.snapshot = structuredClone(snapshot);
    if (snapshot.phase === "settled") this.stopPolling();
    this.publish();
  }

  private acceptEvent(event: SpectatorEvent, publish = true): void {
    this.eventCursor = Math.max(this.eventCursor, event.seq);
    if (this.events.some((candidate) => candidate.seq === event.seq)) return;
    this.events = [...this.events, structuredClone(event)]
      .sort((left, right) => left.seq - right.seq)
      .slice(-MAX_RECENT_EVENTS);
    if (publish && this.snapshot) this.publish();
  }

  private publish(): void {
    if (!this.snapshot) return;
    const frame = this.frame();
    for (const listener of this.listeners) listener(frame);
  }

  private frame(): SpectatorFrame {
    if (!this.snapshot) throw new Error("观战快照尚未就绪");
    return structuredClone({ snapshot: this.snapshot, events: this.events });
  }

  private setState(state: SpectatorConnectionState): void {
    this.state = state;
    for (const listener of this.statusListeners) listener(state);
  }

  private requireResolvedRoomId(): string {
    if (!this.resolvedRoomId) throw new Error("观战成员尚未确认");
    return this.resolvedRoomId;
  }

  private ensureOpen(): void {
    if (this.transportClosed) throw new Error("观战连接已关闭");
  }

  private request(path: string, init: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(
      () => controller.abort(),
      this.requestTimeoutMs,
    );
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${this.token}`);
    return this.fetcher
      .call(globalThis, `${this.apiBaseUrl.replace(/\/$/u, "")}${path}`, {
        ...init,
        headers,
        signal: controller.signal,
      })
      .catch((error: unknown) => {
        throw normalizeError(error, "无法连接观战服务");
      })
      .finally(() => globalThis.clearTimeout(timeout));
  }

  private async requestJson(
    path: string,
    init: RequestInit = {},
    expectedStatus = 200,
  ): Promise<unknown> {
    const response = await this.request(path, init);
    if (!response.ok || response.status !== expectedStatus)
      throw new Error(`观战服务返回 ${response.status}`);
    try {
      return (await response.json()) as unknown;
    } catch {
      throw new Error("观战服务响应不是有效 JSON");
    }
  }
}

export function parseSpectatorEvent(input: unknown): SpectatorEvent | null {
  const parsed = SpectatorEventSchema.safeParse(input);
  if (parsed.success) return parsed.data;
  const type = eventType(input);
  if (
    type !== null &&
    !SPECTATOR_EVENT_TYPES.has(type as SpectatorEvent["type"])
  )
    return null;
  throw new Error("观战事件响应无效");
}

function parseSpectatorSocketMessage(
  input: unknown,
):
  | { readonly type: "SNAPSHOT"; readonly snapshot: SpectatorSnapshot }
  | { readonly type: "EVENT"; readonly event: SpectatorEvent }
  | null {
  const parsed = SpectatorServerMessageSchema.safeParse(input);
  if (parsed.success) return parsed.data;
  if (isRecord(input) && input.type === "EVENT") {
    const event = parseSpectatorEvent(input.event);
    return event ? { type: "EVENT", event } : null;
  }
  throw new Error("观战服务消息无效");
}

function assertSnapshotSemantics(
  snapshot: SpectatorSnapshot,
  expectedRoomId: string,
): SpectatorSnapshot {
  if (snapshot.roomId !== expectedRoomId) throw new Error("观战快照房间不一致");
  const seats = new Set(snapshot.players.map((player) => player.seat));
  if (
    seats.size !== 4 ||
    snapshot.players.some((player) => player.publicId !== `seat-${player.seat}`)
  )
    throw new Error("观战快照公开座位无效");
  return snapshot;
}

function eventType(input: unknown): string | null {
  return isRecord(input) && typeof input.type === "string" ? input.type : null;
}

function isRecord(input: unknown): input is Record<string, unknown> {
  return typeof input === "object" && input !== null && !Array.isArray(input);
}

function exactObject<const Keys extends readonly string[]>(
  input: unknown,
  keys: Keys,
  label: string,
): Record<Keys[number], unknown> {
  if (!isRecord(input)) throw new Error(`${label}无效`);
  const actual = Object.keys(input);
  if (
    actual.length !== keys.length ||
    !actual.every((key) => keys.includes(key))
  )
    throw new Error(`${label}字段无效`);
  return input;
}

function requiredString(input: unknown, label: string): string {
  if (typeof input !== "string" || input.length === 0)
    throw new Error(`${label}无效`);
  return input;
}

function requiredInteger(input: unknown, label: string, min: number): number {
  if (typeof input !== "number" || !Number.isSafeInteger(input) || input < min)
    throw new Error(`${label}无效`);
  return input;
}

function normalizeError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback);
}
